import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AvaliacaoService, ConfiguracaoAvaliacao } from "../../../core/config/services/avaliacao.service";
import { NotaService, LinhaDesempenho, ColunaDesempenho, NotaView, Periodo } from "../services/nota.service";
import { ParecerService } from "../../parecer/services/parecer.service";
import { ContextService } from "../../../core/context/services/context.service";
import { DateUtils } from '../../../shared/date-utils';

interface Turma {
  id: number;
  descricao?: string;
  tipoResultado?: string;
}

interface Componente {
  id: number;
  descricao?: string;
}

interface ConfiguracaoCha {
  permiteUtilizarCompetencias?: boolean;
  permiteUtilizarConhecimentosConteudos?: boolean;
  permiteUtilizarHabilidadesCapacidades?: boolean;
  permiteUtilizarAtitudes?: boolean;
}

@Component({
  selector: 'app-desempenho-ano',
  templateUrl: './desempenho-ano.component.html',
  styleUrls: ['./desempenho-ano.component.scss']
})
export class DesempenhoAnoComponent implements OnInit, OnChanges, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() turma: Turma | null = null;
  @Input() periodo: Periodo | null = null;
  @Input() componente: Componente | null = null;
  @Input() instrumento: unknown = null;
  @Input() configuracaoAvaliacao: ConfiguracaoAvaliacao = {};
  @Input() configuracaoCha: ConfiguracaoCha = {};
  @Input() isLoading = false;
  @Input() colunas: ColunaDesempenho[] = [];
  @Input() linhas: LinhaDesempenho[] = [];
  @Input() hasPermissionsToEdit = false;
  @Input() enumSituacaoMatricula: Record<string, { description?: string; value?: unknown }> = {};
  @Input() ocultarDetalhes = false;
  @Input() semCalendario = false;
  @Input() semPeriodoAvaliativo = false;
  @Input() semDesempenhos = false;

  @Output() salvar = new EventEmitter<NotaView>();
  @Output() showParecer = new EventEmitter<unknown>();
  @Output() showCHA = new EventEmitter<unknown>();
  @Output() showAvaliacaoDescritiva = new EventEmitter<unknown>();
  @Output() togglePublicacao = new EventEmitter<unknown>();
  @Output() showObjetivos = new EventEmitter<unknown>();
  @Output() showAnexos = new EventEmitter<unknown>();

  searchForm: FormGroup;
  filterForm: FormGroup;

  linhasDesempenho: LinhaDesempenho[] = [];
  colunasDesempenho: ColunaDesempenho[] = [];
  pareceres: unknown[] = [];
  cha: unknown[] = [];
  objetivosAprendizagem: unknown[] = [];
  anexos: unknown[] = [];

  showFilters = false;
  showSearch = false;
  selectedTab = 'notas';

  linhasDesempenho$: Observable<LinhaDesempenho[]> = of([]);
  colunasDesempenho$: Observable<ColunaDesempenho[]> = of([]);

  constructor(
    private fb: FormBuilder,
    private avaliacaoService: AvaliacaoService,
    private notaService: NotaService,
    private parecerService: ParecerService,
    private contextService: ContextService
  ) {
    this.searchForm = this.fb.group({
      searchText: ['']
    });

    this.filterForm = this.fb.group({
      situacaoMatricula: [''],
      grupoEnturmacao: [''],
      situacaoItemEducacional: ['']
    });
  }

  ngOnInit(): void {
    this.colunasDesempenho = this.colunas || [];
    this.linhasDesempenho = this.linhas || [];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['colunas']) {
      this.colunasDesempenho = this.colunas || [];
    }
    if (changes['linhas']) {
      this.linhasDesempenho = this.linhas || [];
    }
  }

  getSituacao(linha: LinhaDesempenho): string | null {
    let situacao = linha.enturmacao.situacao || linha.enturmacao.matricula?.situacao;

    const situacoesItens = (linha.enturmacao as any).situacaoItensEducacionais;
    if (this.componente && Array.isArray(situacoesItens)) {
      const sitEspecifica = situacoesItens.find((s: any) => s.itemEducacional?.id === this.componente?.id);
      if (sitEspecifica && sitEspecifica.situacao) {
        situacao = sitEspecifica.situacao;
      }
    }

    if (situacao && this.enumSituacaoMatricula[situacao]) {
      return this.enumSituacaoMatricula[situacao].description || situacao;
    }
    return situacao || null;
  }

  getSituacaoMatriculaBadge(linha: LinhaDesempenho): string {
    const situacoesItens = (linha.enturmacao as any).situacaoItensEducacionais;
    if (this.componente && Array.isArray(situacoesItens)) {
      const sitEspecifica = situacoesItens.find((s: any) => s.itemEducacional?.id === this.componente?.id);
      if (sitEspecifica && sitEspecifica.situacao) {
        return sitEspecifica.situacao;
      }
    }

    return linha.enturmacao.situacao || linha.enturmacao.matricula?.situacao || '';
  }

  onFotoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.src = '/images/default-user.png';
    }
  }

  onSearch(): void {
    const searchText = this.searchForm.get('searchText')?.value;
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      this.linhasDesempenho = this.linhas.filter(linha => {
        const nomeSocial = linha.enturmacao.matricula?.nomeSocial?.toLowerCase() || '';
        const nome = linha.enturmacao.matricula?.nome?.toLowerCase() || '';
        return nomeSocial.includes(searchLower) || nome.includes(searchLower);
      });
    } else {
      this.linhasDesempenho = this.linhas || [];
    }
  }

  onFilter(): void {
    const filters = this.filterForm.value;
    this.linhasDesempenho = this.linhas.filter(linha => {
      if (filters.situacaoMatricula && linha.enturmacao.matricula?.situacao !== filters.situacaoMatricula) {
        return false;
      }
      if (filters.grupoEnturmacao && linha.enturmacao.grupoEnturmacao !== filters.grupoEnturmacao) {
        return false;
      }
      if (filters.situacaoItemEducacional && linha.enturmacao.situacao !== filters.situacaoItemEducacional) {
        return false;
      }
      return true;
    });
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.linhasDesempenho = this.linhas || [];
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  toggleSearch(): void {
    this.showSearch = !this.showSearch;
  }

  selectTab(tab: string): void {
    this.selectedTab = tab;
  }

  get hasItensList(): boolean {
    return this.linhas && this.linhas.length > 0;
  }

  get hasColunas(): boolean {
    return this.colunas && this.colunas.length > 0;
  }

  get hasAnyConfiguracaoCha(): boolean {
    return !!(this.configuracaoCha && (
      this.configuracaoCha.permiteUtilizarCompetencias ||
      this.configuracaoCha.permiteUtilizarConhecimentosConteudos ||
      this.configuracaoCha.permiteUtilizarHabilidadesCapacidades ||
      this.configuracaoCha.permiteUtilizarAtitudes
    ));
  }

  /** Opt-out: exibe parecer sempre que PARECER.visivel não for explicitamente false */
  showParecerMethod(): boolean {
    return this.configuracaoAvaliacao.PARECER?.visivel !== false;
  }

  formatDate(dateString: string): string {
    return DateUtils.formatarDataCurta(dateString);
  }

  onSalvar(nota: NotaView): void {
    this.salvar.emit(nota);
  }

  onShowParecer(nota: NotaView): void {
    this.showParecer.emit(nota);
  }

  onShowAvaliacaoDescritiva(nota: NotaView): void {
    this.showAvaliacaoDescritiva.emit(nota);
  }

  onTogglePublicacao(nota: NotaView): void {
    this.togglePublicacao.emit(nota);
  }

  onShowCHA(nota: NotaView): void {
    this.showCHA.emit(nota);
  }

  onShowObjetivos(nota: NotaView): void {
    this.showObjetivos.emit(nota);
  }

  onShowAnexos(nota: NotaView): void {
    this.showAnexos.emit(nota);
  }

  trackByEnturmacao(index: number, linha: LinhaDesempenho): number {
    return linha.enturmacao.id;
  }

  trackByColuna(index: number, coluna: ColunaDesempenho): string {
    return coluna.rawValue?.id?.toString() || index.toString();
  }

  trackByNota(index: number, nota: NotaView): string {
    return nota.$$nocache || index.toString();
  }

}